import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, get, type Comparison, type Revision } from '../../services/api';
import { DiffView } from '../../components/Diff';
import { Empty, ErrorBox, Loading } from '../../components/Ui';
import { fullDate, shortRev, splitMessage, timeAgo } from '../../shared/utils/format';
import { useAsync, useTitle } from '../../hooks';
import { useRepo } from './RepoLayout';
import { NotFound } from '../workspace/NotFound';
import { Icon } from '../../components/Icon';

export function RepoHistory() {
  const { repo, base } = useRepo();
  useTitle(`History · ${repo.fullName}`);
  const [items, setItems] = useState<Revision[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [error, setError] = useState<ApiError>();
  const [loading, setLoading] = useState(true);
  const latest = useRef(0);
  const retryCursor = useRef<string | undefined>(undefined);

  const load = async (before?: string) => {
    const request = ++latest.current;
    retryCursor.current = before;
    setLoading(true);
    setError(undefined);
    try {
      const page = await get<{ revisions: Revision[]; next: string | null }>(
        `/repositories/${repo.id}/revisions?limit=30${before ? `&before=${encodeURIComponent(before)}` : ''}`,
      );
      if (latest.current !== request) return;
      setItems((previous) =>
        before
          ? [
              ...previous,
              ...page.revisions.filter((revision) => !previous.some((item) => item.id === revision.id)),
            ]
          : page.revisions,
      );
      setNext(page.next);
    } catch (e) {
      if (latest.current === request)
        setError(e instanceof ApiError ? e : new ApiError(0, 'error', 'Something went wrong.'));
    } finally {
      if (latest.current === request) setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    return () => {
      latest.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo.id, repo.head]);

  if (error && !items.length) return <ErrorBox error={error} retry={() => load()} />;
  if (loading && items.length === 0) return <Loading />;
  if (items.length === 0)
    return (
      <div className="card">
        <Empty title="No history yet">A revision is recorded each time changes are pushed.</Empty>
      </div>
    );

  return (
    <div className="stack">
      <div className="section-title">
        <h2>Wiki history</h2>
        <p className="muted">
          Each explicit sync records a revision. Open a message to review its changes, or a revision
          identifier to browse its pages.
        </p>
      </div>
      <div className="card">
        <ul className="rev-list">
          {items.map((revision) => (
            <li className="rev-item" key={revision.id}>
              <Icon name="history" />
              <span className="msg">
                <Link to={`${base}/commit/${revision.id}`} title={revision.message}>
                  {splitMessage(revision.message).title}
                </Link>
              </span>
              <span className="muted">
                <Link to={`/user/${revision.author}`}>{revision.author}</Link> ·{' '}
                <span title={fullDate(revision.createdAt)}>{timeAgo(revision.createdAt)}</span>
              </span>
              {revision.mergedFrom && (
                <span className="badge" title={`Merged ${revision.mergedFrom}`}>
                  merge
                </span>
              )}
              <Link
                className="rev-id"
                to={`${base}?rev=${revision.id}`}
                title="Browse files at this revision"
              >
                {shortRev(revision.id)}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      {error && <ErrorBox error={error} retry={() => void load(retryCursor.current)} />}
      {next && (
        <button className="btn" disabled={loading} onClick={() => load(next)}>
          {loading ? 'Loading…' : 'Load older revisions'}
        </button>
      )}
    </div>
  );
}

export function RepoCommit() {
  const { repo, base } = useRepo();
  const { rev = '' } = useParams();
  useTitle(`${shortRev(rev)} · ${repo.fullName}`);
  const [params, setParams] = useSearchParams();
  // Without `from`, a revision is compared with its parent. With it, any two revisions can be compared.
  const from = params.get('from');
  const revision = useAsync(
    () => get<Revision>(`/repositories/${repo.id}/revisions/${encodeURIComponent(rev)}`),
    [repo.id, rev],
  );
  const diff = useAsync(
    () =>
      get<Comparison>(
        `/repositories/${repo.id}/compare?to=${encodeURIComponent(rev)}${from ? `&from=${encodeURIComponent(from)}` : ''}`,
      ),
    [repo.id, rev, from],
  );
  const others = useAsync(
    () => get<{ revisions: Revision[] }>(`/repositories/${repo.id}/revisions?limit=100`),
    [repo.id],
  );

  if (revision.error?.status === 404) return <NotFound what="revision" />;
  if (revision.error) return <ErrorBox error={revision.error} retry={revision.reload} />;
  const r = revision.data;

  return (
    <div className="stack">
      {!r ? (
        <Loading />
      ) : (
        <div className="card card-pad stack revision-summary">
          <h2 style={{ margin: 0 }}>{splitMessage(r.message).title}</h2>
          {splitMessage(r.message).body && (
            <p className="revision-body">{splitMessage(r.message).body}</p>
          )}
          <div className="muted">
            <Link to={`/user/${r.author}`}>{r.author}</Link> · {fullDate(r.createdAt)} ·{' '}
            <span className="rev-id">{shortRev(r.id)}</span>
            {r.parent && (
              <>
                {' '}
                · parent{' '}
                <Link className="rev-id" to={`${base}/commit/${r.parent}`}>
                  {shortRev(r.parent)}
                </Link>
              </>
            )}
          </div>
          <div className="row">
            <Link className="btn sm" to={`${base}?rev=${r.id}`}>
              Browse files at this revision
            </Link>
            <label className="row" style={{ gap: 6 }}>
              <span className="muted">Compare with</span>
              <select
                className="input"
                style={{ width: 'auto', maxWidth: 260 }}
                value={from ?? ''}
                onChange={(e) => {
                  const next = new URLSearchParams(params);
                  if (e.target.value) next.set('from', e.target.value);
                  else next.delete('from');
                  setParams(next);
                }}
              >
                <option value="">
                  its parent
                  {r.parent ? ` (${shortRev(r.parent)})` : ' (nothing: first revision)'}
                </option>
                {others.data?.revisions
                  .filter((other) => other.id !== r.id)
                  .map((other) => (
                    <option key={other.id} value={other.id}>
                      {shortRev(other.id)} · {splitMessage(other.message).title.slice(0, 40)}
                    </option>
                  ))}
              </select>
            </label>
            {diff.data && (
              <span className="diff-stat muted">
                {diff.data.files.length} {diff.data.files.length === 1 ? 'file' : 'files'} changed ·{' '}
                <span className="add">+{diff.data.additions}</span>{' '}
                <span className="del">−{diff.data.deletions}</span>
              </span>
            )}
          </div>
        </div>
      )}
      {others.error && <ErrorBox error={others.error} retry={others.reload} />}
      {diff.loading && !diff.data ? (
        <Loading />
      ) : diff.error ? (
        <ErrorBox error={diff.error} retry={diff.reload} />
      ) : (
        diff.data && <DiffView files={diff.data.files} truncated={diff.data.truncated} />
      )}
    </div>
  );
}
